import { AsyncPipe, CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest, distinctUntilChanged, filter, map } from 'rxjs';

import { runtimeSelectors } from '@blaze-runtime/engine-data-access';
import {
  COMPLEX_FILTER_SERVICE,
  ComplexFiltersModule,
  LogicalQueryFormGroupValue,
} from '@blaze-runtime/engine-ui-feature';
import {
  DatasetFilterInfo,
  DatasetFilterScope,
  LogicalQueryNode,
} from '@blaze-shared/api-contracts-domain';
import { generalSettingsSelectors } from '@blaze-shared/app-state-data-access';
import { DatasetsApiService } from '@blaze-shared/datasets-data-access';
import { mapLogicalQueryNodeToLogicalPredicateNode } from '@blaze-shared/datasets-domain';
import {
  ContainerElementMetaUiFeatures,
  DataBindingSourceInfo,
  getActiveBinding,
  getBackground,
  GlobalQueryState,
  isGlobalQueryState,
  makeContainerVisualEffectStyles,
  makeLabelHorizontalAlignStyles,
  RenderElementContext,
  SearchAndFiltersMode,
} from '@blaze-shared/elements-domain';
import { NotificationService, syncSelect } from '@blaze-shared/platform-data-access';
import { type ElementSchema } from '@blaze-shared/schema-domain';
import { safeDetectChanges } from '@blaze-shared/shared-ui';
import { isDeepEqual, useEmitTracker } from '@blaze-shared/shared-util';
import { IconsModule } from '@blaze-shared/theming-data-access';
import { isGetNode } from '@blaze-shared/workflow-domain';

import { CanvasElementComponent } from '../../canvas-elements/element-component';
import { DataFilterRenderElementInstance } from '../../canvas-elements/models/render-element.models';
import { FilterBuilderComplexFilterService } from '../../components/complex-filters/filter-builder/filter-builder-complex-filter.service';
import { FilterBuilderComponent } from '../../components/complex-filters/filter-builder/filter-builder.component';
import { FilterBuilderTriggerComponent } from '../../components/complex-filters/filter-builder-trigger/filter-builder-trigger.component';
import { ElementFilterHeaderComponent } from '../../components/element-filter-header/element-filter-header.component';
import { FormStateService } from '../../services/forms/runtime/public-api';
import { CanvasElementBaseComponent } from '../__base/base.component';

@UntilDestroy()
@Component({
  selector: 'be-data-filter',
  templateUrl: './data-filter.component.html',
  styleUrls: ['./data-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    IconsModule,
    ComplexFiltersModule,
    AsyncPipe,
    ElementFilterHeaderComponent,
    FilterBuilderComponent,
    FilterBuilderTriggerComponent,
  ],
  providers: [
    FilterBuilderComplexFilterService,
    {
      provide: COMPLEX_FILTER_SERVICE,
      useExisting: FilterBuilderComplexFilterService,
    },
  ],
})
export class DataFilterComponent
  extends CanvasElementBaseComponent<'dataFilter'>
  implements
    CanvasElementComponent<'dataFilter'>,
    OnInit,
    OnChanges,
    OnDestroy,
    DataFilterRenderElementInstance
{
  private readonly complexFilterService = inject(FilterBuilderComplexFilterService);
  private readonly datasetsApiService = inject(DatasetsApiService);
  private readonly formStateService = inject(FormStateService);
  private readonly notificationService = inject(NotificationService);

  @Input() value!: Record<string, any>;

  @ViewChild('filterBuilder') filterBuilderComponent: FilterBuilderComponent | null = null;

  private readonly dataValue = this.getDataInput('value');
  private readonly outputEmitTracker = useEmitTracker();
  private readonly queryEmitTracker = useEmitTracker();

  private complexFiltersInitialized = false;
  private datasetId?: string;
  private dataUpdateTimeout?: any;
  private projectId: string | undefined = undefined;
  private searchQuery?: LogicalQueryNode | undefined;
  private workflowId?: string;

  data!: Record<string, any>;

  searchMode: SearchAndFiltersMode = SearchAndFiltersMode.PREDEFINED;
  query?: LogicalQueryNode;
  savedFilters: DatasetFilterInfo[] = [];
  savedFiltersLoading = true;
  allowToSavePublicComplexFilters = false;
  runtimeModePublicOrSecure$ = this.store.select(
    generalSettingsSelectors.isRuntimeModePublicOrSecure,
  );

  labelEnabled!: boolean;
  labelText!: string;
  icon!: string;

  titleColor!: string;
  headerStyles!: Record<string, string | undefined>;
  labelStyles!: Record<string, string>;
  wrapperStyles!: Record<string, string | undefined>;
  slotStyles!: Record<string, string | undefined>;

  readonly SearchAndFiltersMode = SearchAndFiltersMode;

  get slotElementContext(): { elementContext: RenderElementContext } {
    return {
      elementContext: {
        extendInputPadding: false,
        disableShadowEffect: true,
        dataContainer: 'form',
      },
    };
  }

  get propsDataSource(): DataBindingSourceInfo | undefined {
    return getActiveBinding(this.props?.data, 'value')?.dataSource;
  }

  get schema(): ElementSchema | undefined {
    return this.getEventSchema('change');
  }

  constructor() {
    super();
    this.initMarginHandler((props) => props?.style?.container?.offset?.margin);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['props']) {
      const { content } = this.props ?? {};
      const { title } = content ?? {};

      this.labelEnabled = title?.enabled ?? true;
      this.icon = title?.icon ?? '';

      this.searchMode =
        this.props?.content.searchAndFilters?.mode ?? SearchAndFiltersMode.PREDEFINED;

      if (this.searchMode === SearchAndFiltersMode.FILTER_BUILDER && this.propsDataSource) {
        this.initComplexFilters(this.propsDataSource.id);
        this.loadSavedComplexFilters();
      }

      this.updateStyles();
    }
    if (!this.bindingResults?.['value']) {
      this.setDefaultEventSchema('change');
    }
  }

  ngOnInit(): void {
    this.getElementUiFeatures<ContainerElementMetaUiFeatures>()
      .pipe(untilDestroyed(this))
      .subscribe(({ title }) => {
        this.labelText = title ?? '';
        safeDetectChanges(this.cdr);
      });

    this.dataValue.schema$.pipe(untilDestroyed(this)).subscribe((value) => {
      if (!value) {
        return;
      }
      this.updateData(value);
      this.setDefaultEventSchema('change', value);
    });

    this.dataValue.data$.pipe(untilDestroyed(this)).subscribe((data: any) => {
      if (!data || !isGlobalQueryState(data)) {
        return;
      }

      this.datasetId = data.datasetId;
      this.workflowId = data.workflowId;
      this.searchQuery = data.query;
      this.updateQueryOutput(data.query);
    });

    this.projectId = syncSelect(this.store, runtimeSelectors.selectProjectId);
  }

  ngOnDestroy(): void {
    this.formStateService.clear(this.elementId);
  }

  dataUpdated(): void {
    // Wait for all the associated filters to emit so that we don't spam events on initial event or when typing in filters
    clearTimeout(this.dataUpdateTimeout);
    this.dataUpdateTimeout = setTimeout(() => {
      this.updateOutput(this.data);
    }, 300);
  }

  applyComplexFilter(formGroupValue: LogicalQueryFormGroupValue): void {
    this.query = formGroupValue;
    if (!this.searchQuery) {
      this.updateQueryOutput(this.query);
      return;
    }
    const query: LogicalQueryNode = {
      ...this.searchQuery,
      children: [...this.searchQuery.children, this.query],
    };
    this.updateQueryOutput(query);
  }

  saveComplexFilter(filter: {
    name: string;
    scope: DatasetFilterScope;
    query: LogicalQueryNode;
  }): void {
    if (!this.projectId) {
      return;
    }

    this.savedFiltersLoading = true;
    this.cdr.markForCheck();

    this.datasetsApiService
      .createDatasetFilter({
        ...filter,
        queryNode: mapLogicalQueryNodeToLogicalPredicateNode(filter.query),
        projectId: this.projectId,
        uiComponentId: this.elementId,
      })
      .subscribe({
        next: (createdFilter) => {
          this.savedFilters = [...this.savedFilters, createdFilter];
          this.savedFiltersLoading = false;
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.notificationService.error(
            error?.error?.errorMessage || 'There was an error. Please try again later.',
            true,
          );
          this.savedFiltersLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  deleteComplexFilter(filter: DatasetFilterInfo): void {
    this.datasetsApiService.deleteDatasetFilter(filter.id).subscribe();
    this.savedFilters = this.savedFilters.filter((f) => f.id !== filter.id);
    this.cdr.markForCheck();
  }

  private initComplexFilters(workflowId: string): void {
    if (this.complexFiltersInitialized) {
      return;
    }

    this.complexFiltersInitialized = true;

    combineLatest({
      getNode: this.store
        .select(runtimeSelectors.selectFirstNodeInWorkflow(workflowId, isGetNode))
        .pipe(distinctUntilChanged(isDeepEqual), filter(Boolean)),
      filterBuilderRestriction: this.props$.pipe(
        map((props) => props?.content.filterBuilderConfig.columns),
        distinctUntilChanged(isDeepEqual),
      ),
    })
      .pipe(untilDestroyed(this))
      .subscribe(({ getNode, filterBuilderRestriction }) => {
        this.complexFilterService.setup(getNode, filterBuilderRestriction);
      });
  }

  private loadSavedComplexFilters(): void {
    this.savedFiltersLoading = true;
    this.datasetsApiService.getDatasetFilters(this.elementId).subscribe((filters) => {
      this.savedFilters = filters;
      this.savedFiltersLoading = false;
      this.cdr.markForCheck();
    });
  }

  private updateStyles(): void {
    const { style, content } = this.props ?? {};
    const { container } = style || {};
    const { title } = content ?? {};
    if (!container) {
      return;
    }

    const headerStyles: Record<string, string | undefined> = {
      // color: container.title.color1,
      marginBottom: this.isUsingOldPaddingStrategy ? '8px' : '16px',
      padding: this.isUsingOldPaddingStrategy ? '0 16px' : undefined,
    };
    const labelStyles: Record<string, string> = {
      ...makeLabelHorizontalAlignStyles(title?.align),
    };

    if (title?.iconPosition === 'right') {
      labelStyles['flex-direction'] = `row-reverse`;
    }

    this.titleColor = container.title.color1;

    const wrapperStyles = {
      padding: container.offset?.padding,
      background: getBackground(container.fill),
      border:
        container?.outline.selectorType !== 'none' && container?.outlineWidth !== '0px'
          ? `${container.outlineWidth} solid ${container.outline.color1}`
          : undefined,
      borderRadius: container.radius !== '0px' ? container.radius : undefined,
      ...makeContainerVisualEffectStyles(container.visualEffect),
    };

    const slotStyles = {
      padding: this.isUsingOldPaddingStrategy ? '16px' : undefined,
    };

    this.headerStyles = headerStyles;
    this.labelStyles = labelStyles;
    this.wrapperStyles = wrapperStyles;
    this.slotStyles = slotStyles;
  }

  private updateData(schema: ElementSchema): void {
    const data: Record<string, any> = {};
    for (const col of schema.columns) {
      data[col.name] =
        this.data?.[col.name] ?? (col.multipleValues || col.dataType === 'Array' ? [] : null);
    }
    this.data = data;
  }

  private updateQueryOutput(query: LogicalQueryNode | undefined = undefined): void {
    const dataSource = this.propsDataSource;
    if (!dataSource || !this.datasetId || !this.workflowId || !this.elementId) {
      return;
    }

    const data: GlobalQueryState = {
      elementId: this.elementId,
      dataSource,
      query,
      datasetId: this.datasetId,
      workflowId: this.workflowId,
    };

    const isInitialData = !this.queryEmitTracker.hasPrevious();
    if (!this.queryEmitTracker.shouldEmit(data)) {
      return;
    }

    this.output.emit({
      eventType: 'query',
      data,
      output: 'data-filter',
      isInitialData,
    });
    this.updateOutput(this.data, !isInitialData);
  }

  private updateOutput(data: any, forceEmit = false): void {
    if (!data) {
      return;
    }

    const isInitialData = !this.outputEmitTracker.hasPrevious();
    const shouldEmit = this.outputEmitTracker.shouldEmit(data);
    if (!shouldEmit && !forceEmit) {
      return;
    }

    this.output.emit({
      eventType: 'change',
      data,
      isInitialData,
      schema: this.schema,
    });
  }
}
